import { useLocation } from 'react-router-dom';
import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner';
import { EmptyState } from '@Components/EmptyState';
import { TableauEmbed } from '@Components/TableauEmbed';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { useTableauEmbedScript } from '@src/utils/hooks/tableau/useTableauEmbedScript';
import { useTableauToken } from '@src/utils/hooks/tableau/useTableauToken';
import { customDashboardSlug } from './custom-dashboard-utils';

interface CustomDashboardBodyProps {
  isLoading: boolean;
  dashboardUrl: string | undefined;
  token: string | undefined;
  company: string;
}

const CustomDashboardBody = ({
  isLoading,
  dashboardUrl,
  token,
  company
}: CustomDashboardBodyProps) => {
  if (isLoading) return <Spinner />;
  if (!dashboardUrl) {
    return (
      <EmptyState
        title='Dashboard not found'
        subtitle="This custom dashboard doesn't exist or is no longer available."
      />
    );
  }
  return (
    <TableauEmbed src={dashboardUrl} token={token}>
      <viz-filter field='client' value={company} />
    </TableauEmbed>
  );
};

export const CustomDashboard = () => {
  const { pathname } = useLocation();
  const slug = pathname.split('/').pop() ?? '';
  const company = useLocationCompany() ?? '';
  const { cid, custom_dashboards: customDashboards } = useCompanyInfo();
  const isScriptLoaded = useTableauEmbedScript();
  const { data: token, isLoading: isTokenLoading } = useTableauToken();

  const dashboard = customDashboards.find(({ name }) => customDashboardSlug(name) === slug);
  // `cid` is empty on the not-yet-loaded fallback (useCompanyInfo's DUMMY_COMPANY_INFO), so a
  // missing match while company info is still loading isn't a real "not found" yet.
  const isLoading = !isScriptLoaded || isTokenLoading || !token || (!dashboard && !cid);

  return (
    <Page
      pageType={dashboard?.name ?? 'Custom Dashboard'}
      title={dashboard?.name ?? 'Custom Dashboard'}
      isLoading={isLoading}
    >
      <CustomDashboardBody
        isLoading={isLoading}
        dashboardUrl={dashboard?.url}
        token={token}
        company={company}
      />
    </Page>
  );
};
